(() => {
if (customElements.get('video-container')) return;

// web component for video player controls
// Usage: <video-container></video-container>
class VideoContainer extends HTMLElement {
  constructor() {
    super();
    this.isMobile = window.innerWidth <= 768;
    this.coverImageSrc = this.dataset.coverImage || null;
  }

  connectedCallback() {
    const video = this.querySelector("video");
    this.addVideoPlayPauseToggle(video);
    this.addSoundMuteUnmuteToggle(video);
    this.handleCoverImage(video);
    // Handle initial visibility based on device type
    this.handleVideoContainerView();


    // Re-evaluate visibility on resize so switching between mobile/desktop doesn't leave a blank
    this._resizeHandler = this._debounce(() => {
      const wasMobile = this.isMobile;
      this.isMobile = window.innerWidth <= 768;
      if (wasMobile !== this.isMobile) {
        this.handleVideoContainerView();
      }
    }, 150);
    window.addEventListener("resize", this._resizeHandler);
  }

  disconnectedCallback() {
    if (this._resizeHandler) {
      window.removeEventListener("resize", this._resizeHandler);
    }
  }

  handleVideoContainerView() {
    if (this.coverImageSrc) return;

    const parentHasMobileVideo = this.closest('.has-mobile-video') !== null;

    if (this.isMobile) {
      // On mobile: hide desktop video only if a mobile-specific video exists
      if (this.classList.contains("banner__media--mobile")) {
        this.classList.remove("hidden");
      } else if (this.classList.contains("banner__media--desktop") && parentHasMobileVideo) {
        this.classList.add("hidden");
      }
    } else {
      // On desktop: hide mobile video, show desktop video
      if (this.classList.contains("banner__media--desktop")) {
        this.classList.remove("hidden");
      } else if (this.classList.contains("banner__media--mobile")) {
        this.classList.add("hidden");
      }
    }
  }

  _debounce(fn, delay) {
    let timer;
    return (...args) => {
      clearTimeout(timer);
      timer = setTimeout(() => fn.apply(this, args), delay);
    };
  }

  addVideoPlayPauseToggle(video) {
    const playPauseBtn = this.querySelector(".video-play-pause-control");
    const playIcon = playPauseBtn?.querySelector(".play-button");
    const pauseIcon = playPauseBtn?.querySelector(".pause-button");

    if (video && playPauseBtn) {
      // Initial setup based on the video playing state
      video.addEventListener("play", () => {
        playIcon.classList.add("display-none"); // Hide play icon
        pauseIcon.classList.remove("display-none"); // Show pause icon
      });

      video.addEventListener("pause", () => {
        playIcon.classList.remove("display-none"); // Show play icon
        pauseIcon.classList.add("display-none"); // Hide pause icon
      });

      // Set initial state based on whether the video is autoplaying or paused
      if (video.paused) {
        playIcon?.classList.remove("display-none"); // Show play icon
        pauseIcon?.classList.add("display-none"); // Hide pause icon
      } else {
        playIcon?.classList.add("display-none"); // Hide play icon
        pauseIcon?.classList.remove("display-none"); // Show pause icon
      }

      // Add event listener to toggle play/pause on button click
      playPauseBtn.addEventListener("click", (event) => {
        event.stopPropagation()
        event.preventDefault();
        if (video.paused) {
          video.play();
        } else {
          video.pause();
        }
      });
    }
  }

  addSoundMuteUnmuteToggle(video) {
    const muteUnmuteBtn = this.querySelector(".video-sound-control");
    const muteIcon = muteUnmuteBtn?.querySelector(".mute-button");
    const soundIcon = muteUnmuteBtn?.querySelector(".sound-button");

    if (video && muteUnmuteBtn) {
      // Add event listener to toggle mute/unmute on button click
      muteUnmuteBtn.addEventListener("click", (event) => {
        event.stopPropagation();
        event.preventDefault();
        if (video.muted) {
          video.muted = false;
          muteIcon?.classList.add("display-none"); // Show mute icon
          soundIcon?.classList.remove("display-none"); // Hide sound icon
        } else {
          video.muted = true;
          muteIcon?.classList.remove("display-none"); // Hide mute icon
          soundIcon?.classList.add("display-none"); // Show sound icon
        }
      });
    }
  }

  handleCoverImage(video) {
    const coverImage = this.querySelector(".video-container__cover-image");
    if (!video || !coverImage) return;

    const hideCover = () => {
      coverImage.classList.add("is-hidden");
    };

    // Hide cover image when video starts playing
    video.addEventListener("playing", hideCover);

    // Also hide if video can play through and is not paused (autoplay case)
    video.addEventListener("canplaythrough", () => {
      if (!video.paused) {
        hideCover();
      }
    });

    // If video is already playing (e.g., autoplay already started)
    if (!video.paused) {
      hideCover();
    }
  }
}
customElements.define('video-container', VideoContainer);
})();
